import { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutlet, useParams } from 'react-router-dom';
import { mutate } from 'swr';
import CommonError from '@components/_common/common-error/CommonError';
import { Divider } from '@components/_common/divider/Divider.styled';
import MainContainer from '@components/_common/main-container/MainContainer';
import PullToRefresh from '@components/_common/pull-to-refresh/PullToRefresh';
import CheckInPostStories from '@components/check-in-posts/CheckInPostStories';
import UserHeader from '@components/header/user-header/UserHeader';
import NoteSection from '@components/note/note-section/NoteSection';
import AllPostSection from '@components/post/AllPostSection';
import Profile from '@components/profile/Profile';
import UserMoreModal from '@components/user-page/UserMoreModal';
import { UserPageContext } from '@components/user-page/UserPage.context';
import { useIsPreviewMode } from '@components/view-as/PreviewModeContext';
import {
  BOTTOM_TABBAR_HEIGHT,
  TITLE_HEADER_HEIGHT,
  VIEW_AS_BANNER_HEIGHT,
} from '@constants/layout';
import { MAIN_SCROLL_CONTAINER_ID } from '@constants/scroll';
import { Layout } from '@design-system';
import useAsyncEffect from '@hooks/useAsyncEffect';
import { useDwellTime } from '@hooks/useDwellTime';
import { useTrackEvent } from '@hooks/useTrackEvent';
import { useBoundStore } from '@stores/useBoundStore';
import { UserSelector } from '@stores/user';
import { readFriendCheckIn } from '@utils/apis/checkIn';
import { userListApiPrefixForViewer } from '@utils/apis/userApiPrefix';

interface UserPageProps {
  usernameOverride?: string;
}

function UserPage({ usernameOverride }: UserPageProps = {}) {
  const params = useParams();
  const username = usernameOverride ?? params.username;
  const { myProfile } = useBoundStore((state) => ({ myProfile: state.myProfile }));
  const isMyPage = username === myProfile?.username;
  const previewMode = useIsPreviewMode();
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { featureFlags } = useBoundStore(UserSelector);
  const trackEvent = useTrackEvent();

  // Source-of-navigation tracking: callers can pass `state: { source: 'X' }`
  // to react-router's navigate(). Tells us "this profile visit came from
  // the shared playlist" vs "the discover feed" vs "search". Backend can't
  // see the referrer because the profile fetch URL is identical regardless.
  const navSource: string =
    typeof (location.state as { source?: unknown } | null)?.source === 'string'
      ? ((location.state as { source: string }).source as string)
      : 'unknown';

  useEffect(() => {
    trackEvent('profile_visited', {
      is_my_page: isMyPage ? 'true' : 'false',
      preview_mode: previewMode ? 'true' : 'false',
      source: navSource,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Total profile-page dwell. screen_dwell already covers route-level
  // time, but UserPage is special because its sub-routes (notes, posts,
  // reactions) keep the user "on the profile" conceptually. This event
  // collapses all of that into one number per visit.
  useDwellTime('profile_dwell', {
    is_my_page: isMyPage ? 'true' : 'false',
  });

  const { user, refreshAfterFriendshipChange } = useContext(UserPageContext);
  const userId = user.data?.id;
  const unreadCount = user.data?.unread_chat_count;

  const readCheckIn = async () => {
    if (!username || !user.data || !user.data.check_in.id) return;
    await readFriendCheckIn(user.data.check_in.id);
  };

  useAsyncEffect(readCheckIn, [user.data]);

  const outlet = useOutlet();

  useEffect(() => {
    if (isMyPage && !previewMode) {
      navigate('/my');
    }
  }, [isMyPage, navigate, previewMode]);

  const handleClickMore = () => {
    setShowMore(true);
  };

  const handleRefresh = async () => {
    if (!username) return;

    const encodedUsername = encodeURIComponent(username);
    const p = userListApiPrefixForViewer(featureFlags?.postsVerQ, myProfile?.current_ver);

    if (featureFlags?.questionResponseFeature) {
      await Promise.all([mutate(`${p}user/${encodedUsername}/all-posts/`)]);
    } else {
      await Promise.all([mutate(`${p}user/${encodedUsername}/notes/`)]);
    }
  };

  return (
    <>
      <MainContainer
        key={username}
        style={{ height: '100vh', overflow: 'hidden', display: outlet ? 'none' : 'block' }}
      >
        {!previewMode && (
          <UserHeader
            username={username}
            onClickMore={handleClickMore}
            userId={userId}
            unreadCount={unreadCount}
          />
        )}
        <div
          id={MAIN_SCROLL_CONTAINER_ID}
          style={{
            height: previewMode
              ? `calc(100vh - ${BOTTOM_TABBAR_HEIGHT}px - ${VIEW_AS_BANNER_HEIGHT}px)`
              : `calc(100vh - ${TITLE_HEADER_HEIGHT}px - ${BOTTOM_TABBAR_HEIGHT}px)`,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            width: '100%',
            marginTop: previewMode ? 0 : `${TITLE_HEADER_HEIGHT}px`,
            paddingBottom: '24px', // 하단 여유 공간 추가
            position: 'relative',
          }}
        >
          <PullToRefresh onRefresh={handleRefresh}>
            <Layout.FlexCol w="100%" bgColor="LIGHT">
              {user.state === 'hasError' && <CommonError />}
              {user.state === 'hasValue' && user.data && (
                <UserMoreModal
                  isVisible={showMore}
                  setIsVisible={setShowMore}
                  user={user.data}
                  callback={refreshAfterFriendshipChange}
                />
              )}
              {(user.state === 'loading' || user.state === 'hasValue') && (
                <>
                  {/** profile section */}
                  <Layout.FlexRow
                    w="100%"
                    alignItems="center"
                    justifyContent="space-between"
                    p={12}
                    bgColor="WHITE"
                    rounded={8}
                  >
                    <Profile user={user.data} />
                  </Layout.FlexRow>
                  {featureFlags?.checkInPosts && userId && (
                    <CheckInPostStories authorUserId={userId} authorUsername={username} />
                  )}
                  <Divider width={8} bgColor="LIGHT" />
                  <Layout.FlexCol pt={8} pl={12} pb="default" w="100%" bgColor="WHITE" rounded={8}>
                    {featureFlags?.questionResponseFeature ? (
                      <AllPostSection username={username} />
                    ) : (
                      <NoteSection username={username} />
                    )}
                  </Layout.FlexCol>
                </>
              )}
            </Layout.FlexCol>
          </PullToRefresh>
        </div>
      </MainContainer>
      <Outlet />
    </>
  );
}

export default UserPage;
